import { useEffect, useState } from "react";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { useAuth } from "@/providers/auth-provider";
import { getIdeaBoard } from "@/services/ideas/get-idea-board";
import { createIdea } from "@/services/ideas/create-idea";
import { voteIdea } from "@/services/ideas/vote-idea";
import { claimIdeaReward } from "@/services/ideas/claim-reward";
import { LoadingOverlay } from "@/components/ui/overlays/loading-overlay";
import { IdeaData, GetIdeaBoardOutput } from "types/web-app";
import { IdeaCard } from "./idea-card";
import { CreateIdeaModal } from "./create-idea-modal";

export const IdeaBoardView = () => {
  const { getAccessToken } = useAuth();
  const [boardData, setBoardData] = useState<GetIdeaBoardOutput | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [showVoteConfirmation, setShowVoteConfirmation] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    fetchBoard();
  }, []);

  const fetchBoard = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    setIsLoading(true);
    try {
      const response = await getIdeaBoard(accessToken);
      if (response.ok && response.data) {
        setBoardData(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch idea board:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVote = async (ideaId: number, score: number) => {
    const accessToken = getAccessToken();
    if (!accessToken || isVoting) return;

    setIsVoting(true);
    try {
      const payload =
        boardData?.mode === "VOTING"
          ? { voting_score: score }
          : { priority_score: score };

      const response = await voteIdea(accessToken, {
        votes: [{ idea_id: ideaId, ...payload }],
      });
      if (response.ok && response.data) {
        const { votes_today, reward_available } = response.data;

        // Show confirmation before removing from list
        setShowVoteConfirmation(true);

        setTimeout(() => {
          setShowVoteConfirmation(false);
          // Remove from list if in VOTING mode
          if (boardData?.mode === "VOTING") {
            setBoardData((prevBoard) =>
              prevBoard
                ? {
                    ...prevBoard,
                    ideas: prevBoard.ideas.filter((i) => i.id !== ideaId),
                    votes_today: votes_today ?? prevBoard.votes_today,
                    reward_available:
                      reward_available ?? prevBoard.reward_available,
                  }
                : prevBoard,
            );
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Failed to vote:", error);
      setMessage({ text: "Failed to vote", type: "error" });
      setTimeout(() => setMessage(null), 2000);
    } finally {
      setIsVoting(false);
    }
  };

  const handleClaimReward = async () => {
    const accessToken = getAccessToken();
    if (!accessToken || isClaiming) return;

    setIsClaiming(true);
    try {
      const response = await claimIdeaReward(accessToken);
      if (response.ok && response.data) {
        setMessage({
          text: response.data.message || "Reward claimed!",
          type: "success",
        });
        setBoardData((prev) =>
          prev
            ? { ...prev, reward_claimed: true, reward_available: false }
            : prev,
        );
      } else {
        setMessage({
          text: response.message || "Failed to claim reward",
          type: "error",
        });
      }
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error("Failed to claim reward:", error);
      setMessage({ text: "Failed to claim reward", type: "error" });
      setTimeout(() => setMessage(null), 2000);
    } finally {
      setIsClaiming(false);
    }
  };

  const handleCreateIdea = async (title: string, content: string) => {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    try {
      const response = await createIdea(accessToken, { title, content });
      if (response.ok) {
        setMessage({
          text: "Idea submitted! It will be reviewed by admins.",
          type: "success",
        });
        setShowCreateModal(false);
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({
          text: response.message || "Failed to submit",
          type: "error",
        });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (error) {
      console.error("Failed to create idea:", error);
      setMessage({ text: "Failed to submit idea", type: "error" });
      setTimeout(() => setMessage(null), 2000);
    }
  };

  const currentIdea =
    boardData?.mode === "VOTING" ? boardData.ideas[0] : null;

  const totalVotesToday = boardData?.votes_today || 0;
  const votesNeeded = Math.max(0, 3 - totalVotesToday);

  return (
    <div className="w-full flex flex-col gap-3 pb-18 p-2 bg-pk-night-1/60 h-full backdrop-blur-[2px]">
      {/* Header */}
      <div className="flex items-center gap-2 justify-between bg-pk-purple-3 rounded-lg px-2 xs:px-4 py-1 xs:py-2 border-3 xs:border-4 border-pk-purple-5 shadow-cartoon-purple">
        <div className="flex flex-col">
          <span className="text-pk-gray-1 font-bold text-xl xs:text-2xl leading-tight drop-shadow-sm">
            💡 Ideas Board
          </span>
          <span className="text-pk-gray-1/70 text-xs xs:text-sm font-semibold">
            {boardData?.mode === "VOTING"
              ? "Rate ideas from 1-5"
              : "Prioritize top ideas"}
          </span>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-pk-purple-1 rounded-md px-2 xs:px-3 py-1 xs:py-2 flex items-center gap-2 border-2 border-pk-purple-4 hover:bg-pk-purple-2 transition"
        >
          <span className="text-pk-gray-4 font-bold text-sm xs:text-base">
            + Propose
          </span>
        </button>
      </div>

      {/* Reward Banner */}
      {boardData && !boardData.reward_claimed && (
        <StandardFrame variant="gold" className="p-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-pk-gray-4 font-bold text-sm">
                  Daily Voter Reward
                </span>
                <span className="bg-pk-gold-4/30 text-pk-gold-6 text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-pk-gold-4/50">
                  {totalVotesToday}/3
                </span>
              </div>
              <span className="text-pk-gray-3 text-xs">
                {boardData.reward_available
                  ? "Ready to claim!"
                  : `Vote on ${votesNeeded} more idea${
                      votesNeeded !== 1 ? "s" : ""
                    } to unlock`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <img
                src="/assets/app-ui/poke-coin.png"
                width={24}
                height={24}
                alt="PokeCoins"
              />
              <span className="text-pk-gold-5 font-bold">50</span>
              {boardData.reward_available && (
                <button
                  onClick={handleClaimReward}
                  disabled={isClaiming}
                  className="bg-pk-green-3 text-pk-gray-1 font-bold px-3 py-1 rounded-md border-2 border-pk-green-5 hover:bg-pk-green-4 transition disabled:opacity-50"
                >
                  {isClaiming ? "..." : "Claim"}
                </button>
              )}
            </div>
          </div>
        </StandardFrame>
      )}

      {/* Message Toast */}
      {message && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-lg font-bold text-sm shadow-xl transition-all animate-bounce ${
            message.type === "success"
              ? "bg-pk-green-3 text-pk-gray-1 border-2 border-pk-green-5"
              : "bg-pk-red-3 text-pk-gray-1 border-2 border-pk-red-5"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Main Content */}
      <StandardFrame
        variant="purple"
        className="flex-1 overflow-hidden relative min-h-[300px]"
      >
        <LoadingOverlay isVisible={isLoading} message="Loading ideas..." />

        {!isLoading && boardData?.mode === "VOTING" && (
          <div className="flex flex-col h-full p-2">
            {showVoteConfirmation ? (
              <div className="flex-1 flex flex-col items-center justify-center animate-in fade-in zoom-in duration-300">
                <span className="text-6xl mb-4">✅</span>
                <span className="text-pk-gray-1 font-bold text-2xl">
                  Vote Recorded!
                </span>
                <span className="text-pk-purple-1 text-sm mt-2">
                  Loading next idea...
                </span>
              </div>
            ) : currentIdea ? (
              <>
                <div className="flex-1 flex items-center justify-center">
                  <IdeaCard idea={currentIdea} />
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <span className="text-pk-gray-3 text-center text-sm">
                    How important is this idea?
                  </span>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((score) => (
                      <button
                        key={score}
                        onClick={() => handleVote(currentIdea.id, score)}
                        disabled={isVoting}
                        className={`w-12 h-12 rounded-lg font-bold text-lg border-3 transition hover:scale-105 disabled:opacity-50 ${
                          score <= 2
                            ? "bg-pk-red-2 border-pk-red-4 text-pk-red-5"
                            : score === 3
                            ? "bg-pk-gold-2 border-pk-gold-4 text-pk-gold-5"
                            : "bg-pk-green-2 border-pk-green-4 text-pk-green-5"
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-pk-gray-4 px-2">
                    <span>Not needed</span>
                    <span>Must have!</span>
                  </div>
                </div>
                <div className="text-center text-pk-gray-4 text-xs mt-2">
                  {boardData.ideas.length} ideas remaining
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-pk-gray-4">
                <div className="text-center">
                  <span className="text-4xl mb-2 block">🎉</span>
                  <span className="font-bold">All caught up!</span>
                  <p className="text-sm mt-1">
                    You've voted on all available ideas.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {!isLoading && boardData?.mode === "RANKING" && (
          <div className="flex flex-col h-full p-2 gap-2 overflow-y-auto">
            <span className="text-pk-gray-3 text-sm text-center mb-2">
              Drag to reorder by priority (coming soon)
            </span>
            {boardData.ideas.map((idea, index) => (
              <div key={idea.id} className="flex items-center gap-2">
                <span className="text-pk-purple-5 font-bold w-6 text-center">
                  #{index + 1}
                </span>
                <IdeaCard idea={idea} compact />
              </div>
            ))}
          </div>
        )}
      </StandardFrame>

      {/* Create Idea Modal */}
      {showCreateModal && (
        <CreateIdeaModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateIdea}
        />
      )}
    </div>
  );
};
